import { v4 as uuidv4 } from "uuid";
import { getTabNonce } from "./recorderPersistence";

/**
 * Cross-tab recording session protocol (EN-1609), over BroadcastChannel.
 *
 * A recording paused (or running) in one tab is invisible to a second tab, which would start a new
 * recording and strand the first. A tab holding a session answers "who-holds-session" probes from
 * other tabs, and can be asked to stop and save its session. Like the liveness probe, every message
 * carries the sender's tab nonce: a channel never hears its own post, but a tab's *other* channel
 * instances do, so responders ignore their own tab's nonce.
 */
export const SESSION_CHANNEL = "happydoc-recording-session";

export type HeldSession = {
    nonce: string;
    state: "recording" | "paused";
    visitId: string;
    patientName: string | null;
    // The transport the session records on. Extension side panels share one offscreen recorder, so
    // the start gate treats a same-visit session on it like any other. Absent from older builds.
    transport?: "browser" | "extension";
};

type SessionMessage =
    | { kind: "who-holds-session"; nonce: string }
    | ({ kind: "session-held" } & HeldSession)
    | { kind: "stop-and-save"; targetNonce: string; requestId: string }
    | { kind: "stop-and-save-done"; requestId: string; ok: boolean }
    | { kind: "session-ended"; nonce: string };

const asMessage = (data: unknown): SessionMessage | null =>
    typeof data === "object" && data !== null && typeof (data as { kind?: unknown }).kind === "string"
        ? (data as SessionMessage)
        : null;

/** Collects every session held by *other* tabs that replies within `timeoutMs`. Resolves [] without BroadcastChannel. */
export function findSessionsElsewhere(timeoutMs = 250): Promise<HeldSession[]> {
    if (typeof BroadcastChannel === "undefined") return Promise.resolve([]);
    return new Promise((resolve) => {
        const channel = new BroadcastChannel(SESSION_CHANNEL);
        const found: HeldSession[] = [];
        channel.onmessage = (event: MessageEvent) => {
            const msg = asMessage(event.data);
            if (msg?.kind === "session-held") {
                const { nonce, state, visitId, patientName, transport } = msg;
                found.push({ nonce, state, visitId, patientName, ...(transport ? { transport } : {}) });
            }
        };
        channel.postMessage({ kind: "who-holds-session", nonce: getTabNonce() } satisfies SessionMessage);
        setTimeout(() => {
            channel.close();
            resolve(found);
        }, timeoutMs);
    });
}

/** Asks `target`'s tab to stop and save its session. Resolves true only on a successful reply; false on failure, timeout, or no BroadcastChannel. */
export function requestStopAndSave(target: HeldSession, timeoutMs = 15_000): Promise<boolean> {
    if (typeof BroadcastChannel === "undefined") return Promise.resolve(false);
    return new Promise((resolve) => {
        const channel = new BroadcastChannel(SESSION_CHANNEL);
        const requestId = uuidv4();
        const finish = (ok: boolean) => {
            clearTimeout(timer);
            channel.close();
            resolve(ok);
        };
        channel.onmessage = (event: MessageEvent) => {
            const msg = asMessage(event.data);
            if (msg?.kind === "stop-and-save-done" && msg.requestId === requestId) finish(msg.ok === true);
        };
        channel.postMessage({
            kind: "stop-and-save",
            targetNonce: target.nonce,
            requestId,
        } satisfies SessionMessage);
        const timer = setTimeout(() => finish(false), timeoutMs);
    });
}

/**
 * Installs this tab's responder. Answers other tabs' probes while `getSession()` is non-null, and
 * runs `stopAndSave` only for requests targeting this tab. Returns cleanup (no-op without BroadcastChannel).
 */
export function createSessionResponder(
    getSession: () => Omit<HeldSession, "nonce"> | null,
    stopAndSave: () => Promise<void>,
): () => void {
    if (typeof BroadcastChannel === "undefined") return () => {};
    const nonce = getTabNonce();
    const channel = new BroadcastChannel(SESSION_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
        const msg = asMessage(event.data);
        if (!msg) return;
        if (msg.kind === "who-holds-session" && msg.nonce !== nonce) {
            const session = getSession();
            if (session) channel.postMessage({ kind: "session-held", ...session, nonce } satisfies SessionMessage);
        } else if (msg.kind === "stop-and-save" && msg.targetNonce === nonce) {
            const reply = (ok: boolean) =>
                channel.postMessage({
                    kind: "stop-and-save-done",
                    requestId: msg.requestId,
                    ok,
                } satisfies SessionMessage);
            stopAndSave().then(
                () => reply(true),
                () => reply(false),
            );
        }
    };
    return () => channel.close();
}

/** Tells other tabs this tab's session is gone (call on pagehide while a session is held). */
export function announceSessionEnded(): void {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(SESSION_CHANNEL);
    channel.postMessage({ kind: "session-ended", nonce: getTabNonce() } satisfies SessionMessage);
    channel.close();
}

/** Listens for other tabs' session-ended announcements. Returns cleanup. */
export function onSessionEnded(listener: () => void): () => void {
    if (typeof BroadcastChannel === "undefined") return () => {};
    const nonce = getTabNonce();
    const channel = new BroadcastChannel(SESSION_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
        const msg = asMessage(event.data);
        if (msg?.kind === "session-ended" && msg.nonce !== nonce) listener();
    };
    return () => channel.close();
}
